import { Link } from '@inertiajs/react'
import { Database, ExternalLink, Plus, Trash2, Workflow, X } from 'lucide-react'
import { useEffect, useState, type ReactNode } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { FdeEditorPanel } from '@/frontend/features/Fde/FdeEditorSurface'
import { stateLabel, stateVariants } from '@/frontend/features/Fde/ProcessDataCoveragePanel'
import { fdeProjectPath, fdeRoutes } from '@/frontend/features/Fde/routes'
import SemanticObjectPreview from '@/frontend/features/Fde/SemanticObjectPreview'
import { cn } from '@/lib/utils'
import type {
  ActionType,
  Evidence,
  FdeCopy,
  ModelElement,
  ProcessCanvasNodeDefinition,
  ProcessDataCoverage,
  ProcessEvidenceState,
  ProcessNodeKind,
  ProcessOntologyRef,
} from '@/frontend/features/Fde/types'

interface Props {
  projectId: string
  node: ProcessCanvasNodeDefinition
  dataCoverage: ProcessDataCoverage['nodes'][number] | null
  elements: ModelElement[]
  evidences: Evidence[]
  actionTypes: ActionType[]
  copy: FdeCopy
  onChange: (node: ProcessCanvasNodeDefinition) => void
  onDelete: () => void
  onClose: () => void
}

const nodeKinds: ProcessNodeKind[] = ['trigger', 'activity', 'decision', 'wait', 'handoff', 'event', 'subprocess', 'end', 'gap']
const evidenceStates: ProcessEvidenceState[] = ['reported', 'observed', 'data_confirmed', 'contested', 'unknown']
const contextRoles: ProcessOntologyRef['role'][] = ['owner', 'participant', 'system', 'input', 'output', 'rule', 'metric', 'problem', 'objective', 'opportunity', 'result']
const dataRoles = new Set<ProcessOntologyRef['role']>(['input', 'output', 'metric'])

export default function ProcessNodeInspector(props: Props) {
  const [elementId, setElementId] = useState('')
  const [role, setRole] = useState<ProcessOntologyRef['role']>('participant')
  const [evidenceId, setEvidenceId] = useState('')
  const [expandedRequirement, setExpandedRequirement] = useState<string | null>(null)
  const linked = new Set(props.node.ontology_refs.map((reference) => reference.element_id))
  const availableElements = props.elements.filter((element) => !linked.has(String(element.id)))
  const availableEvidences = props.evidences.filter((evidence) => !props.node.evidence_ids.includes(String(evidence.id)))
  const initialRequirementId = String(props.dataCoverage?.requirements.find((requirement) => requirement.has_primary_key && requirement.state !== 'missing')?.element_id || '')

  useEffect(() => {
    setExpandedRequirement(initialRequirementId || null)
  }, [initialRequirementId, props.node.id])

  function addContext() {
    if (!elementId) return
    props.onChange({ ...props.node, ontology_refs: [...props.node.ontology_refs, { element_id: elementId, role }] })
    setElementId('')
  }

  function addEvidence() {
    if (!evidenceId) return
    props.onChange({ ...props.node, evidence_ids: [...props.node.evidence_ids, evidenceId] })
    setEvidenceId('')
  }

  return (
    <FdeEditorPanel
      title={props.copy.workspace.process_step_detail}
      icon={<Workflow />}
      closeLabel={props.copy.common.close}
      onClose={props.onClose}
      bodyClassName="space-y-7"
      testId="process-node-editor"
      footer={(
        <Button variant="ghost" className="text-destructive hover:text-destructive" onClick={props.onDelete}>
          <Trash2 />{props.copy.workspace.process_delete_step}
        </Button>
      )}
    >
      <div className="grid min-w-0 gap-5 sm:grid-cols-2">
        <Field className="sm:col-span-2" label={props.copy.workspace.process_step_name}>
          <Input value={props.node.label} onChange={(event) => props.onChange({ ...props.node, label: event.target.value })} />
        </Field>
        <Field label={props.copy.workspace.process_step_type}>
          <Select value={props.node.kind} onValueChange={(value) => props.onChange({ ...props.node, kind: value as ProcessNodeKind })}>
            <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
            <SelectContent>{nodeKinds.map((kind) => <SelectItem key={kind} value={kind}>{props.copy.workspace[`process_node_kind_${kind}`]}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label={props.copy.workspace.process_evidence_state}>
          <Select value={props.node.confidence} onValueChange={(value) => props.onChange({ ...props.node, confidence: value as ProcessEvidenceState })}>
            <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
            <SelectContent>{evidenceStates.map((state) => <SelectItem key={state} value={state}>{props.copy.workspace[`process_evidence_${state}`]}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field className="sm:col-span-2" label={props.copy.forms.description}>
          <Textarea value={props.node.description || ''} rows={4} onChange={(event) => props.onChange({ ...props.node, description: event.target.value })} />
        </Field>
        <Field className="sm:col-span-2" label={props.copy.workspace.process_action_type}>
          <Select value={String(props.node.action_type_id || '__none__')} onValueChange={(value) => props.onChange({ ...props.node, action_type_id: value === '__none__' ? null : value })}>
            <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="__none__">{props.copy.common.none}</SelectItem>{props.actionTypes.map((actionType) => <SelectItem key={actionType.id} value={String(actionType.id)}>{actionType.name}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
      </div>

      <Section title={props.copy.workspace.process_context_links}>
        <OntologyReferenceItems node={props.node} elements={props.elements} copy={props.copy} onChange={props.onChange} />
        <div className="grid min-w-0 gap-2 sm:grid-cols-[minmax(0,1fr)_minmax(10rem,0.55fr)_auto]">
          <Select value={elementId} onValueChange={setElementId}><SelectTrigger className="w-full min-w-0"><SelectValue placeholder={props.copy.workspace.process_select_context} /></SelectTrigger><SelectContent>{availableElements.map((element) => <SelectItem key={element.id} value={String(element.id)}>{element.name} · {element.ontology_class?.name || element.kind}</SelectItem>)}</SelectContent></Select>
          <Select value={role} onValueChange={(value) => setRole(value as ProcessOntologyRef['role'])}><SelectTrigger className="w-full min-w-0"><SelectValue /></SelectTrigger><SelectContent>{contextRoles.map((item) => <SelectItem key={item} value={item}>{props.copy.workspace[`process_role_${item}`]}</SelectItem>)}</SelectContent></Select>
          <Button className="w-full sm:w-9" size="icon" variant="outline" disabled={!elementId} onClick={addContext}><Plus /></Button>
        </div>
      </Section>

      <Section title={props.copy.workspace.process_data_coverage}>
        {!props.dataCoverage || props.dataCoverage.requirements.length === 0 ? (
          <p className="text-xs leading-5 text-muted-foreground">{props.copy.workspace.process_data_step_empty}</p>
        ) : (
          <div className="divide-y divide-border overflow-hidden rounded-lg border border-border bg-background">
            {props.dataCoverage.requirements.map((requirement) => (
              <div key={requirement.requirement_key} className="px-3 py-3">
                <div className="flex flex-wrap items-center gap-2">
                  <Database className="size-3.5 text-muted-foreground" />
                  <strong className="text-xs">{requirement.name}</strong>
                  <Badge variant={stateVariants[requirement.state]} className="text-[9px]">{stateLabel(requirement.state, props.copy)}</Badge>
                </div>
                <p className="mt-1.5 text-[10px] text-muted-foreground">{requirement.sources.length > 0 ? requirement.sources.map((source) => source.name).join(' · ') : props.copy.workspace.process_data_no_backing}</p>
                <p className="mt-1 text-[10px] text-muted-foreground">{requirement.required_properties.length > 0 ? requirement.required_properties.map((property) => property.label).join(' · ') : props.copy.workspace.process_data_identity_only}</p>
                {requirement.available_properties > 0 && <p className="mt-1 text-[10px] text-muted-foreground">{props.copy.workspace.process_data_properties}: {requirement.mapped_properties}/{requirement.available_properties}</p>}
                {requirement.has_primary_key && requirement.state !== 'missing' && <button type="button" className="mt-2 text-[10px] font-semibold text-primary hover:underline" onClick={() => setExpandedRequirement((current) => current === String(requirement.element_id) ? null : String(requirement.element_id))}>{expandedRequirement === String(requirement.element_id) ? props.copy.workspace.semantic_objects_hide : props.copy.workspace.semantic_objects_show}</button>}
                {expandedRequirement === String(requirement.element_id) && <div className="mt-3"><SemanticObjectPreview projectId={props.projectId} classId={requirement.ontology_class.id} copy={props.copy} limit={5} compact /></div>}
                {requirement.has_primary_key && requirement.state !== 'missing' && <Link className="mt-2 inline-flex items-center gap-1 text-[10px] font-semibold text-primary hover:underline" href={`${fdeProjectPath(fdeRoutes.data, props.projectId)}?data_tab=explore&data_view=objects&object_type_id=${requirement.ontology_class.id}`}><ExternalLink className="size-3" />{props.copy.workspace.semantic_open_explorer}</Link>}
              </div>
            ))}
          </div>
        )}
        <Button size="sm" variant="outline" asChild>
          <Link href={fdeProjectPath(fdeRoutes.data, props.projectId)}><ExternalLink />{props.copy.workspace.process_data_open_lineage}</Link>
        </Button>
      </Section>

      <Section title={props.copy.workspace.process_evidences}>
        <LinkedItems items={props.node.evidence_ids.map((id) => ({ key: id, label: props.evidences.find((evidence) => String(evidence.id) === id)?.title || id, remove: () => props.onChange({ ...props.node, evidence_ids: props.node.evidence_ids.filter((candidate) => candidate !== id) }) }))} />
        <div className="flex min-w-0 gap-2">
          <Select value={evidenceId} onValueChange={setEvidenceId}><SelectTrigger className="min-w-0 flex-1"><SelectValue placeholder={props.copy.workspace.process_select_evidence} /></SelectTrigger><SelectContent>{availableEvidences.map((evidence) => <SelectItem key={evidence.id} value={String(evidence.id)}>{evidence.title}</SelectItem>)}</SelectContent></Select>
          <Button size="icon" variant="outline" disabled={!evidenceId} onClick={addEvidence}><Plus /></Button>
        </div>
      </Section>
    </FdeEditorPanel>
  )
}

function OntologyReferenceItems({ node, elements, copy, onChange }: { node: ProcessCanvasNodeDefinition; elements: ModelElement[]; copy: FdeCopy; onChange: Props['onChange'] }) {
  if (node.ontology_refs.length === 0) return null

  function updateProperty(index: number, propertyKey: string, checked: boolean) {
    const references = node.ontology_refs.map((reference, itemIndex) => {
      if (itemIndex !== index) return reference

      const keys = new Set(reference.property_keys || [])
      if (checked) keys.add(propertyKey)
      else keys.delete(propertyKey)
      const propertyKeys = [...keys]
      return { ...reference, property_keys: propertyKeys.length > 0 ? propertyKeys : undefined }
    })
    onChange({ ...node, ontology_refs: references })
  }

  return <div className="space-y-2">{node.ontology_refs.map((reference, index) => {
    const element = elements.find((candidate) => String(candidate.id) === String(reference.element_id))
    const properties = dataRoles.has(reference.role) ? element?.ontology_class?.property_schema || [] : []
    return <div key={`${reference.element_id}:${index}`} className="rounded-lg border border-border bg-background px-3 py-2 text-xs">
      <div className="flex items-center gap-2"><span className="min-w-0 flex-1 truncate"><strong>{element?.name || reference.element_id}</strong><span className="text-muted-foreground"> · {copy.workspace[`process_role_${reference.role}`]}</span></span><button type="button" onClick={() => onChange({ ...node, ontology_refs: node.ontology_refs.filter((_, itemIndex) => itemIndex !== index) })}><X className="size-3" /></button></div>
      {properties.length > 0 && <details className="mt-2 border-t border-border pt-2"><summary className="cursor-pointer text-[10px] font-medium text-primary">{copy.workspace.process_data_properties_select}: {reference.property_keys?.length || copy.workspace.process_data_identity_only}</summary><div className="mt-2 grid gap-2 sm:grid-cols-2">{properties.map((property) => <label key={property.key} className="flex min-w-0 items-start gap-2 text-[10px] text-muted-foreground"><Checkbox checked={reference.property_keys?.includes(property.key) || false} onCheckedChange={(checked) => updateProperty(index, property.key, checked === true)} /><span><strong className="block font-medium text-foreground">{property.label}</strong>{property.description && <span className="line-clamp-2">{property.description}</span>}</span></label>)}</div></details>}
    </div>
  })}</div>
}

function Field({ label, children, className }: { label: string; children: ReactNode; className?: string }) {
  return <label className={cn('grid min-w-0 gap-1.5 text-xs font-medium text-muted-foreground', className)}><span>{label}</span>{children}</label>
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return <section className="grid min-w-0 gap-3 rounded-xl border border-border bg-muted/10 p-4"><h4 className="text-[10px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">{title}</h4>{children}</section>
}

function LinkedItems({ items }: { items: Array<{ key: string; label: string; detail?: string; remove: () => void }> }) {
  if (items.length === 0) return null
  return <div className="space-y-2">{items.map((item) => <div key={item.key} className="flex items-center gap-2 rounded-lg border border-border bg-background px-3 py-2 text-xs"><span className="min-w-0 flex-1 truncate"><strong>{item.label}</strong>{item.detail && <> · {item.detail}</>}</span><button type="button" onClick={item.remove}><X className="size-3" /></button></div>)}</div>
}
